import React, { useState, useEffect } from "react";
import { Users, Warehouse, BarChart3, Settings as SettingsIcon, Download, Truck, History, LogOut, Zap, ArrowRightLeft, ClipboardCheck, ClipboardList, Tag, Bell, BellOff, Share2, Upload } from "lucide-react";

import Dashboard        from "./components/Dashboard";
import CustomerList     from "./components/CustomerList";
import InventoryPanel   from "./components/InventoryPanel";
import Delivery         from "./components/Delivery";
import Collection       from "./components/Collection";
import Settings         from "./components/Settings";
import InitialStock     from "./components/InitialStock";
import CylinderEditor  from "./components/CylinderEditor";
import Login            from "./components/Login";
import ChargingPanel    from "./components/ChargingPanel";
import WarehouseTransfer from "./components/WarehouseTransfer";
import InspectionPanel  from "./components/InspectionPanel";
import OrderManagement  from "./components/OrderManagement";
import DriverMobileView from "./components/DriverMobileView";
import FileShare        from "./components/FileShare";
import { saveToServer, loadAllFromServer, onSaveError, hasPendingBackup, getPendingBackupKeys, readBackup } from './api';

// ── 초기 기본값 (서버에 저장된 데이터가 없을 때만 사용) ──────────
const INITIAL_WAREHOUSES = ["제1 창고(메인)"];

const INITIAL_ITEMS = [
  { id: 1, name: 'LPG 20kg',   unit: '본', standardPrice: 0 },
  { id: 2, name: 'LPG 50kg',   unit: '본', standardPrice: 0 },
  { id: 3, name: '산소(O2)',   unit: '본', standardPrice: 0 },
  { id: 4, name: '질소(N2)',   unit: '본', standardPrice: 0 },
  { id: 5, name: '아르곤(Ar)', unit: '본', standardPrice: 0 },
];

// ── 역할별 탭 ────────────────────────────────────────────────
const TABS = [
  { id: 'dashboard',  icon: BarChart3,      label: '경영현황',    roles: ['admin', 'subadmin', 'staff'] },
  { id: 'customers',  icon: Users,          label: '거래처현황',  roles: ['admin', 'subadmin', 'staff'] },
  { id: 'inventory',  icon: Warehouse,      label: '재고/현황판', roles: ['admin', 'subadmin', 'staff'] },
  { id: 'charging',   icon: Zap,            label: '용기충전',    roles: ['admin', 'subadmin'] },
  { id: 'inspection', icon: ClipboardCheck, label: '용기검사',    roles: ['admin', 'subadmin'] },
  { id: 'orders',     icon: ClipboardList,  label: '주문관리',    roles: ['admin', 'subadmin'] },
  { id: 'delivery',   icon: Truck,          label: '배송등록',    roles: ['admin', 'subadmin'] },
  { id: 'collection', icon: Download,       label: '매입등록',    roles: ['admin', 'subadmin'] },
  { id: 'transfer',   icon: ArrowRightLeft, label: '창고이동',    roles: ['admin', 'subadmin'] },
  { id: 'initial',    icon: History,        label: '이월재고',    roles: ['admin'] },
  { id: 'cylinders',  icon: Tag,            label: '용기수정',    roles: ['admin'] },
  { id: 'fileshare',  icon: Share2,         label: '파일공유',    roles: ['admin', 'subadmin'] },
  { id: 'settings',   icon: SettingsIcon,   label: '설정',        roles: ['admin'] },
];

const ROLE_LABEL = { admin: '관리자', subadmin: '부관리자', staff: '직원', driver: '배달기사' };
const ROLE_COLOR = { admin: '#185FA5', subadmin: '#7C3AED', staff: '#059669', driver: '#D97706' };

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map(c => c.charCodeAt(0)));
}

function useNotification() {
  const [notifStatus, setNotifStatus] = useState(() => {
    if (!('Notification' in window)) return 'unsupported';
    return Notification.permission; // 'default' | 'granted' | 'denied'
  });

  const subscribe = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;
    try {
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') { setNotifStatus('denied'); return; }
      setNotifStatus('granted');
      const reg = await navigator.serviceWorker.ready;
      const keyRes = await fetch('/api/push/vapid-public-key');
      const { publicKey } = await keyRes.json();
      const sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
      await fetch('/api/push/subscribe', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(sub),
      });
    } catch (e) {
      console.warn('푸시 구독 실패:', e);
    }
  };

  const unsubscribe = async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const sub = await reg.pushManager.getSubscription();
      if (sub) {
        await fetch('/api/push/subscribe', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ endpoint: sub.endpoint }),
        });
        await sub.unsubscribe();
      }
      setNotifStatus('default');
    } catch (e) {
      console.warn('구독 해제 실패:', e);
    }
  };

  const toggle = () => {
    if (notifStatus === 'granted') unsubscribe();
    else subscribe();
  };

  return { notifStatus, toggle };
}

export default function App() {
  const isMobile = useIsMobile();
  const { notifStatus, toggle: toggleNotif } = useNotification();
  const [tab, setTab] = useState("dashboard");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [serverError, setServerError] = useState(false);
  const [saveErrorMsg, setSaveErrorMsg] = useState('');
  const [pendingBackup, setPendingBackup] = useState(false);

  // 세션 복원
  const [currentUser, setCurrentUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('gas_user') || 'null'); }
    catch { return null; }
  });

  // ── 공유 상태 ────────────────────────────────────────────
  const [customers,     setCustomers]     = useState([]);
  const [cylinders,     setCylinders]     = useState([]);
  const [deliveries,    setDeliveries]    = useState([]);
  const [collections,   setCollections]   = useState([]);
  const [warehouses,    setWarehouses]    = useState(INITIAL_WAREHOUSES);
  const [items,         setItems]         = useState(INITIAL_ITEMS);
  const [specialPrices, setSpecialPrices] = useState([]);
  const [transfers,         setTransfers]         = useState([]);
  const [inspectionHistory, setInspectionHistory] = useState([]);
  const [orders,        setOrders]        = useState([]);
  const [stockAdjustments, setStockAdjustments] = useState({});
  const [chargingHistory,    setChargingHistory]    = useState([]);
  const [extChargingHistory, setExtChargingHistory] = useState([]);

  // ── 저장 실패 콜백 등록 ─────────────────────────────────
  useEffect(() => {
    onSaveError((key) => {
      setSaveErrorMsg(`⚠️ 서버 저장 실패! 데이터가 임시 보관됩니다. 네트워크 또는 서버 상태를 확인하세요.`);
      setPendingBackup(true);
    });
    if (hasPendingBackup()) setPendingBackup(true);
  }, []);

  // 백업 데이터 복원
  const restoreFromBackup = () => {
    const keys = getPendingBackupKeys();
    const setterMap = {
      deliveries: setDeliveries,
      collections: setCollections,
      customers: setCustomers,
      cylinders: setCylinders,
      items: setItems,
      specialPrices: setSpecialPrices,
      warehouses: setWarehouses,
      transfers: setTransfers,
      inspectionHistory: setInspectionHistory,
      orders: setOrders,
      stockAdjustments: setStockAdjustments,
      chargingHistory: setChargingHistory,
      extChargingHistory: setExtChargingHistory,
    };
    keys.forEach(key => {
      const data = readBackup(key);
      if (data !== null && setterMap[key]) setterMap[key](data);
    });
    setSaveErrorMsg('✅ 백업 데이터가 복원되었습니다. 잠시 후 자동 저장됩니다.');
    setPendingBackup(false);
  };

  // ── 서버(NAS JSON 파일)에서 데이터 로드 (최초 1회) ─────
  useEffect(() => {
    loadAllFromServer()
      .then(d => {
        if (d.customers     !== null) setCustomers(d.customers);
        if (d.cylinders     !== null) setCylinders(d.cylinders);
        if (d.deliveries    !== null) setDeliveries(d.deliveries);
        if (d.collections   !== null) setCollections(d.collections);
        if (d.warehouses    !== null) setWarehouses(d.warehouses);
        if (d.items         !== null) setItems(d.items);
        if (d.specialPrices !== null) setSpecialPrices(d.specialPrices);
        if (d.transfers          != null) setTransfers(d.transfers);
        if (d.inspectionHistory  != null) setInspectionHistory(d.inspectionHistory);
        if (d.orders             != null) setOrders(d.orders);
        if (d.stockAdjustments   != null) setStockAdjustments(d.stockAdjustments);
        if (d.chargingHistory    != null) setChargingHistory(d.chargingHistory);
        if (d.extChargingHistory != null) setExtChargingHistory(d.extChargingHistory);
        setDataLoaded(true);
      })
      .catch(() => {
        setServerError(true);
        setDataLoaded(true);
      });
  }, []);

  // ── 상태 변경 시 자동 저장 (dataLoaded 제외: 초기 로드 시 빈 값 덮어쓰기 방지) ──
  useEffect(() => { if (dataLoaded) saveToServer('customers',     customers);     }, [customers]);     // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('cylinders',     cylinders);     }, [cylinders]);     // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('deliveries',    deliveries);    }, [deliveries]);    // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('collections',   collections);   }, [collections]);   // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('warehouses',    warehouses);    }, [warehouses]);    // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('items',         items);         }, [items]);         // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('specialPrices', specialPrices); }, [specialPrices]); // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('transfers',         transfers);         }, [transfers]);         // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('inspectionHistory', inspectionHistory); }, [inspectionHistory]); // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('orders',            orders);            }, [orders]);            // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('stockAdjustments',  stockAdjustments);  }, [stockAdjustments]);  // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('chargingHistory',    chargingHistory);    }, [chargingHistory]);    // eslint-disable-line
  useEffect(() => { if (dataLoaded) saveToServer('extChargingHistory', extChargingHistory); }, [extChargingHistory]); // eslint-disable-line

  // ── 데이터 백업 다운로드 ─────────────────────────────────
  const downloadBackup = () => {
    const data = {
      exportedAt: new Date().toISOString(),
      customers, cylinders, deliveries, collections,
      warehouses, items, specialPrices, transfers,
      inspectionHistory, orders, stockAdjustments,
      chargingHistory, extChargingHistory,
    };
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `daesung-backup-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // ── 백업 파일 복원 ───────────────────────────────────────
  const restoreBackup = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const d = JSON.parse(ev.target.result);
        if (d.customers)        setCustomers(d.customers);
        if (d.cylinders)        setCylinders(d.cylinders);
        if (d.deliveries)       setDeliveries(d.deliveries);
        if (d.collections)      setCollections(d.collections);
        if (d.warehouses)       setWarehouses(d.warehouses);
        if (d.items)            setItems(d.items);
        if (d.specialPrices)    setSpecialPrices(d.specialPrices);
        if (d.transfers)        setTransfers(d.transfers);
        if (d.inspectionHistory) setInspectionHistory(d.inspectionHistory);
        if (d.orders)           setOrders(d.orders);
        if (d.stockAdjustments) setStockAdjustments(d.stockAdjustments);
        if (d.chargingHistory)    setChargingHistory(d.chargingHistory);
        if (d.extChargingHistory) setExtChargingHistory(d.extChargingHistory);
        alert(`백업 복원 완료!\n백업 일시: ${d.exportedAt ? new Date(d.exportedAt).toLocaleString() : '알 수 없음'}`);
      } catch {
        alert('백업 파일 형식이 올바르지 않습니다.');
      }
      e.target.value = '';
    };
    reader.readAsText(file);
  };

  const handleLogin = (user) => {
    sessionStorage.setItem('gas_user', JSON.stringify(user));
    setCurrentUser(user);
    setTab('dashboard');
  };

  const handleLogout = () => {
    sessionStorage.removeItem('gas_user');
    setCurrentUser(null);
  };

  if (!dataLoaded) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Pretendard, sans-serif', background: '#f8f7f2' }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#185FA5', letterSpacing: '-0.5px' }}>DAESUNG ENERGY</div>
        <div style={{ fontSize: 14, color: '#aaa', marginTop: 10 }}>데이터 불러오는 중...</div>
      </div>
    </div>
  );

  if (serverError) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'Pretendard, sans-serif', background: '#f8f7f2' }}>
      <div style={{ textAlign: 'center', background: 'white', borderRadius: 16, padding: '40px 48px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        <div style={{ fontSize: 24, fontWeight: 900, color: '#185FA5', letterSpacing: '-0.5px' }}>DAESUNG ENERGY</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#e53e3e', marginTop: 20 }}>서버에 연결할 수 없습니다</div>
        <div style={{ fontSize: 13, color: '#666', marginTop: 8, lineHeight: 1.6 }}>
          NAS 서버가 실행 중인지 확인하세요.<br />
          <code style={{ background: '#f1f1f1', padding: '2px 6px', borderRadius: 4 }}>docker-compose up -d</code> 또는{' '}
          <code style={{ background: '#f1f1f1', padding: '2px 6px', borderRadius: 4 }}>npm run serve</code>
        </div>
        <button onClick={() => window.location.reload()} style={{ marginTop: 20, padding: '10px 24px', background: '#185FA5', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' }}>
          다시 시도
        </button>
      </div>
    </div>
  );

  if (!currentUser) return <Login onLogin={handleLogin} />;

  // 배달기사는 전용 모바일 화면으로 라우팅
  if (currentUser.role === 'driver') {
    return (
      <DriverMobileView
        currentUser={currentUser}
        orders={orders}
        setOrders={setOrders}
        cylinders={cylinders}
        setCylinders={setCylinders}
        deliveries={deliveries}
        setDeliveries={setDeliveries}
        collections={collections}
        setCollections={setCollections}
        customers={customers}
        items={items}
        specialPrices={specialPrices}
        warehouses={warehouses}
        onLogout={handleLogout}
        notifStatus={notifStatus}
        onToggleNotif={toggleNotif}
      />
    );
  }

  const visibleTabs = TABS.filter(t => t.roles.includes(currentUser.role));
  const safeTab = visibleTabs.find(t => t.id === tab) ? tab : visibleTabs[0].id;

  const outerPad   = isMobile ? '0' : '24px 40px';
  const innerPad   = isMobile ? '14px 12px' : '24px';
  const logoSize   = isMobile ? 16 : 22;
  const tabMinW    = isMobile ? '72px' : '110px';
  const tabPadV    = isMobile ? '13px' : '18px';
  const tabFontSz  = isMobile ? 11 : 13;
  const tabIconSz  = isMobile ? 14 : 16;

  return (
    <div style={{ fontFamily: "Pretendard, sans-serif", background: "#f8f7f2", minHeight: "100vh", padding: outerPad }}>
      <div style={{ maxWidth: isMobile ? '100%' : 1400, margin: "0 auto" }}>

        {/* 저장 오류 배너 */}
        {saveErrorMsg && (
          <div style={{ marginBottom: 12, padding: '12px 16px', background: saveErrorMsg.startsWith('✅') ? '#f0fdf4' : '#fef2f2', border: `1px solid ${saveErrorMsg.startsWith('✅') ? '#86efac' : '#fca5a5'}`, borderRadius: 10, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: saveErrorMsg.startsWith('✅') ? '#15803d' : '#dc2626', flex: 1 }}>{saveErrorMsg}</span>
            {pendingBackup && (
              <button onClick={restoreFromBackup}
                style={{ padding: '6px 14px', background: '#dc2626', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
                백업 데이터 복원
              </button>
            )}
            <button onClick={() => setSaveErrorMsg('')}
              style={{ padding: '6px 10px', background: 'none', border: '1px solid #d3d1c7', borderRadius: 7, fontSize: 12, color: '#666', cursor: 'pointer' }}>
              닫기
            </button>
          </div>
        )}

        {/* 헤더 */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isMobile ? 12 : 24, padding: isMobile ? '12px 14px 0' : 0 }}>
          <h1 style={{ margin: 0, fontSize: logoSize, fontWeight: 800 }}>
            DAESUNG ENERGY <span style={{ color: "#185FA5", fontWeight: 400 }}>| MES</span>
          </h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 }}>
            {!isMobile && (
              <div style={{ textAlign: 'right' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#1e293b' }}>{currentUser.name}</div>
                <div style={{ fontSize: 12, color: ROLE_COLOR[currentUser.role], fontWeight: 600 }}>{ROLE_LABEL[currentUser.role]}</div>
              </div>
            )}
            {isMobile && (
              <div style={{ fontSize: 12, color: ROLE_COLOR[currentUser.role], fontWeight: 700 }}>{currentUser.name}</div>
            )}
            {notifStatus !== 'unsupported' && (
              <button onClick={toggleNotif} title={notifStatus === 'granted' ? '알림 끄기' : '배송 알림 켜기'}
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: isMobile ? 34 : 38, height: isMobile ? 34 : 38, background: notifStatus === 'granted' ? '#185FA5' : 'white', color: notifStatus === 'granted' ? 'white' : '#999', border: '1px solid #d3d1c7', borderRadius: 8, cursor: 'pointer', padding: 0 }}>
                {notifStatus === 'granted' ? <Bell size={isMobile ? 14 : 16} /> : <BellOff size={isMobile ? 14 : 16} />}
              </button>
            )}
            {currentUser?.role === 'admin' && (
              <>
                <button onClick={downloadBackup} title="데이터 백업 다운로드"
                  style={{ display: 'flex', alignItems: 'center', gap: 4, padding: isMobile ? '6px 10px' : '8px 14px', background: '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: isMobile ? 12 : 13, fontWeight: 600, cursor: 'pointer' }}>
                  <Download size={isMobile ? 13 : 14} />{!isMobile && ' 백업'}
                </button>
                <label title="백업 파일 복원" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: isMobile ? '6px 10px' : '8px 14px', background: 'white', color: '#D97706', border: '1px solid #D97706', borderRadius: 8, fontSize: isMobile ? 12 : 13, fontWeight: 600, cursor: 'pointer' }}>
                  <Upload size={isMobile ? 13 : 14} />{!isMobile && ' 복원'}
                  <input type="file" accept=".json" onChange={restoreBackup} style={{ display: 'none' }} />
                </label>
              </>
            )}
            <button onClick={handleLogout} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: isMobile ? '6px 10px' : '8px 14px', background: 'white', color: '#555', border: '1px solid #d3d1c7', borderRadius: 8, fontSize: isMobile ? 12 : 13, fontWeight: 600, cursor: 'pointer' }}>
              <LogOut size={isMobile ? 13 : 14} />{!isMobile && ' 로그아웃'}
            </button>
          </div>
        </div>

        {/* 탭 + 콘텐츠 */}
        <div style={{ background: "white", border: isMobile ? 'none' : "1px solid #d3d1c7", borderRadius: isMobile ? 0 : 16, overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.05)" }}>
          {/* 탭 바 — 모바일에서는 하단 고정 */}
          {isMobile ? (
            <div style={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 100, background: '#fcfbf7', borderTop: '1px solid #d3d1c7', display: 'flex', overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              {visibleTabs.map(t => (
                <MobileTabBtn key={t.id} icon={t.icon} label={t.label}
                  active={safeTab === t.id} onClick={() => setTab(t.id)}
                  minW={tabMinW} padV={tabPadV} fontSize={tabFontSz} iconSize={tabIconSz} />
              ))}
            </div>
          ) : (
            <div style={{ display: "flex", background: "#fcfbf7", borderBottom: "1px solid #d3d1c7", overflowX: "auto" }}>
              {visibleTabs.map(t => (
                <MobileTabBtn key={t.id} icon={t.icon} label={t.label}
                  active={safeTab === t.id} onClick={() => setTab(t.id)}
                  minW={tabMinW} padV={tabPadV} fontSize={tabFontSz} iconSize={tabIconSz} />
              ))}
            </div>
          )}

          {/* 콘텐츠 — 모바일 하단 탭 바 높이만큼 패딩 */}
          <div style={{ padding: innerPad, paddingBottom: isMobile ? 80 : innerPad }}>
            {safeTab === "dashboard"  && <Dashboard deliveries={deliveries} collections={collections} customers={customers} cylinders={cylinders} />}
            {safeTab === "customers"  && <CustomerList customers={customers} deliveries={deliveries} collections={collections} cylinders={cylinders} />}
            {safeTab === "inventory"  && <InventoryPanel cylinders={cylinders} setCylinders={setCylinders} customers={customers} warehouses={warehouses} setWarehouses={setWarehouses} items={items} stockAdjustments={stockAdjustments} />}
            {safeTab === "charging"   && <ChargingPanel cylinders={cylinders} setCylinders={setCylinders} items={items} setCollections={setCollections} history={chargingHistory} setHistory={setChargingHistory} extHistory={extChargingHistory} setExtHistory={setExtChargingHistory} />}
            {safeTab === "inspection" && <InspectionPanel cylinders={cylinders} setCylinders={setCylinders} items={items} warehouses={warehouses} inspectionHistory={inspectionHistory} setInspectionHistory={setInspectionHistory} setCollections={setCollections} />}
            {safeTab === "orders"     && <OrderManagement customers={customers} items={items} orders={orders} setOrders={setOrders} cylinders={cylinders} setCylinders={setCylinders} deliveries={deliveries} setDeliveries={setDeliveries} specialPrices={specialPrices} />}
            {safeTab === "delivery"   && <Delivery customers={customers} cylinders={cylinders} setCylinders={setCylinders} deliveries={deliveries} setDeliveries={setDeliveries} items={items} warehouses={warehouses} specialPrices={specialPrices} />}
            {safeTab === "collection" && <Collection customers={customers} cylinders={cylinders} setCylinders={setCylinders} collections={collections} setCollections={setCollections} warehouses={warehouses} items={items} />}
            {safeTab === "transfer"   && <WarehouseTransfer cylinders={cylinders} setCylinders={setCylinders} warehouses={warehouses} items={items} transfers={transfers} setTransfers={setTransfers} />}
            {safeTab === "initial"    && <InitialStock customers={customers} cylinders={cylinders} setCylinders={setCylinders} warehouses={warehouses} items={items} stockAdjustments={stockAdjustments} setStockAdjustments={setStockAdjustments} />}
            {safeTab === "cylinders"  && <CylinderEditor cylinders={cylinders} setCylinders={setCylinders} customers={customers} warehouses={warehouses} items={items} />}
            {safeTab === "fileshare"  && <FileShare />}
            {safeTab === "settings"   && <Settings currentUser={currentUser} customers={customers} setCustomers={setCustomers} items={items} setItems={setItems} specialPrices={specialPrices} setSpecialPrices={setSpecialPrices} setCylinders={setCylinders} setDeliveries={setDeliveries} setCollections={setCollections} setWarehouses={setWarehouses} />}
          </div>
        </div>
      </div>
    </div>
  );
}

const MobileTabBtn = ({ icon: Icon, label, active, onClick, minW, padV, fontSize, iconSize }) => (
  <button onClick={onClick} style={{
    flex: 1, minWidth: minW, padding: `${padV} 5px`, border: "none",
    background: active ? "white" : "transparent", color: active ? "#185FA5" : "#777",
    borderBottom: active ? "3px solid #185FA5" : "3px solid transparent",
    cursor: "pointer", display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 3,
    fontSize: fontSize, fontWeight: active ? 700 : 400, whiteSpace: "nowrap",
  }}>
    <Icon size={iconSize} />
    {label}
  </button>
);
